import { c as _c } from "react/compiler-runtime";
import { createUploadManager, waitForConnection } from "@context-providers/file-upload/upload-manager-factory";
import { UploadManagerWorker } from "@context-providers/file-upload/upload-manager-worker";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import { store } from "@store/store";
import { useAppDispatch } from "@store/store-helper";
import { uploadTaskByIdSelector } from "@store/upload-tasks/upload-tasks-selector";
import { removeOne, setOne, updateOne } from "@store/upload-tasks/upload-tasks-slice";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * The File Upload Context provides an object that store all ongoing
 * file uploads and allows to manage them.
 *
 * WARNING: this type should not be used directly, it is only needed to implement
 * the FileUploadContextProvider and the hooks useFileUploader and useCancelUpload.
 */

/** Context which provides the file upload functionalities */
const FileUploadContext = createContext(undefined);

/**
 * @returns a context useful to start, store monitor and complete file upload tasks.
 *
 * WARNING: this type should not be used directly, it is just exported
 * as an implementation detail to realize the FileUploadContextProvider.
 */
export function useFileUploadContext() {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error("FileUploadContext is not initialized.");
  }
  return context;
}

/**
 * @returns a provider component for the FileUploadContext
 * All children of this component may start, monitor, and finish file uploads
 * and the uploads are stored in the context, therefore they outlive any particular
 * mode or component.
 */
export function FileUploadContextProvider(t0) {
  const $ = _c(21);
  const {
    children
  } = t0;
  const dispatch = useAppDispatch();
  const [uploadManagerFallback, setUploadManagerFallback] = useState(undefined);
  let t1;
  if ($[0] !== dispatch) {
    t1 = (id, file, isSilent, context) => {
      const isoDate = new Date().toISOString();
      const fileUploadTask = {
        id,
        fileName: file.name,
        createdAt: isoDate,
        status: BackgroundTaskState.created,
        progress: 0,
        context,
        isSilent
      };
      dispatch(setOne(fileUploadTask));
    };
    $[0] = dispatch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const startTaskInStore = t1;
  const getTaskFromStore = _temp;
  let t2;
  if ($[2] !== dispatch) {
    t2 = (id_1, propsToUpdate) => {
      const task = uploadTaskByIdSelector(id_1);
      if (task === undefined) {
        return;
      }
      dispatch(updateOne({
        id: id_1,
        changes: propsToUpdate
      }));
    };
    $[2] = dispatch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const updateTaskInStore = t2;
  let t3;
  if ($[4] !== dispatch) {
    t3 = id_2 => {
      dispatch(removeOne(id_2));
    };
    $[4] = dispatch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const removeTaskFromStore = t3;
  let t4;
  if ($[6] !== removeTaskFromStore || $[7] !== startTaskInStore || $[8] !== updateTaskInStore) {
    t4 = createUploadManager(getTaskFromStore, startTaskInStore, updateTaskInStore, removeTaskFromStore);
    $[6] = removeTaskFromStore;
    $[7] = startTaskInStore;
    $[8] = updateTaskInStore;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const uploadManager = t4;
  let t5;
  let t6;
  if ($[10] !== removeTaskFromStore || $[11] !== startTaskInStore || $[12] !== updateTaskInStore || $[13] !== uploadManager) {
    t5 = () => {
      const connect = async function connect() {
        if (uploadManager instanceof UploadManagerWorker) {
          const fallback = await waitForConnection(getTaskFromStore, startTaskInStore, updateTaskInStore, removeTaskFromStore, uploadManager);
          if (fallback !== undefined) {
            setUploadManagerFallback(fallback);
          }
        }
      };
      connect();
    };
    t6 = [removeTaskFromStore, setUploadManagerFallback, getTaskFromStore, startTaskInStore, uploadManager, updateTaskInStore];
    $[10] = removeTaskFromStore;
    $[11] = startTaskInStore;
    $[12] = updateTaskInStore;
    $[13] = uploadManager;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  const t7 = uploadManagerFallback ?? uploadManager;
  let t8;
  if ($[16] !== t7) {
    t8 = {
      uploadManager: t7
    };
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== children || $[19] !== t8) {
    t9 = <FileUploadContext.Provider value={t8}>{children}</FileUploadContext.Provider>;
    $[18] = children;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
function _temp(id_0) {
  const selector = uploadTaskByIdSelector(id_0);
  return selector(store.getState());
}